import { Button, Kind } from '@thanx/uikit/button'
import { useStyletron } from '@thanx/uikit/theme'
import { alert } from 'actions/flash'
import { locationsApi, type UpdateLocationPayload } from 'api/locations'
import { getErrorMessage, isError, isFetchBaseQueryError } from 'api/utils'
import palette from 'constants/palette'
import useDispatch from 'hooks/useDispatch'
import { buildTranslate } from 'locales'
import type { Fields as Location } from 'models/Location'
import React, { useEffect, useRef, useState } from 'react'
import { useDisplayError } from 'utilities/displayError'
import { phoneNumberFormatter } from 'utilities/formatters'

const t = buildTranslate('locations.location_list.details_modal')
const tShared = buildTranslate('shared_words')

// Validation mirrors the legacy Formsy `isPhone` rule — 10 digits after
// stripping country code and non-digits. Sharing one helper between the
// validation check and the payload normalisation means the API can
// never receive an 11-digit string that passed a 10-digit validation.
const normalisePhone = (raw: string) =>
  raw.replace(/\D+/g, '').replace(/^1/, '')
const isValidPhone = (raw: string) => normalisePhone(raw).length === 10

// Editable form omits the "1 " country-code prefix the read view shows
// so a no-op Save round-trips 10 digits to the BE, not 11.
const formatForInput = (raw: string): string => phoneNumberFormatter(raw) ?? ''

// Render-state pair returned by `usePhoneField`. The caller drops these
// straight into a Section component (trailing slot + body) — keeping the
// JSX complexity inside the hook and out of the modal's render function.
export type PhoneFieldRender = {
  trailing: React.ReactNode
  body: React.ReactNode
}

// Owns the inline phone-edit state for the Location details modal.
// Returning the two rendered nodes (edit-button trailing slot + read/
// edit body) keeps the modal's main function a flat composition of
// sections, with the phone section's edit-mode branching contained
// here.
const usePhoneField = (location: Location): PhoneFieldRender => {
  const [css] = useStyletron()
  const [isEditing, setIsEditing] = useState(false)
  const [phoneDraft, setPhoneDraft] = useState(formatForInput(location.phone))
  const [phoneError, setPhoneError] = useState<string | null>(null)
  const [updateLocation, { isLoading: isSaving }] =
    locationsApi.useUpdateLocationMutation()
  const dispatch = useDispatch()
  const displayError = useDisplayError()

  const editFormRef = useRef<HTMLDivElement | null>(null)
  const phoneInputRef = useRef<HTMLInputElement | null>(null)

  // Move focus to the input on edit-mode entry.
  useEffect(() => {
    if (isEditing) phoneInputRef.current?.focus()
  }, [isEditing])

  // Reset edit state when the modal swaps to a different location.
  // Today's call sites close+reopen between rows, but reusing the
  // instance would otherwise leak the previous draft into the next row.
  useEffect(() => {
    setPhoneDraft(formatForInput(location.phone))
    setPhoneError(null)
    setIsEditing(false)
  }, [location.id, location.phone])

  // Outside-click cancels the edit — matches the Figma spec (no explicit
  // Cancel button). Suspend while a save is in flight so a stray click
  // doesn't drop the pending mutation's UI affordance.
  useEffect(() => {
    if (!isEditing || isSaving) return undefined
    const onDocMouseDown = (e: MouseEvent) => {
      if (!editFormRef.current?.contains(e.target as Node)) {
        setIsEditing(false)
        setPhoneError(null)
      }
    }
    document.addEventListener('mousedown', onDocMouseDown)
    return () => document.removeEventListener('mousedown', onDocMouseDown)
  }, [isEditing, isSaving])

  const startEditing = () => {
    setPhoneDraft(formatForInput(location.phone))
    setPhoneError(null)
    setIsEditing(true)
  }

  const handleSave = async () => {
    if (!isValidPhone(phoneDraft)) {
      setPhoneError(t('phone_invalid'))
      return
    }
    const payload: UpdateLocationPayload = {
      id: location.id,
      phone: normalisePhone(phoneDraft),
    }
    const response = await updateLocation(payload)
    if (isError(response)) {
      const error = response.error
      if (isFetchBaseQueryError(error) && error.status === 400) {
        setPhoneError(getErrorMessage(error, t('phone_save_failed')))
      } else {
        displayError(response, t('phone_save_failed'))
      }
      return
    }
    dispatch(alert({ key: 'success', message: t('phone_saved'), timeout: 5 }))
    setIsEditing(false)
  }

  const trailing = isEditing ? null : (
    <button
      type="button"
      onClick={startEditing}
      className={css({
        display: 'inline-flex',
        alignItems: 'center',
        gap: '4px',
        background: 'none',
        border: 'none',
        cursor: 'pointer',
        color: palette.belizeHole,
        fontFamily: 'Lato, sans-serif',
        fontWeight: 700,
        fontSize: '16px',
        lineHeight: '20px',
        letterSpacing: '0.25px',
        padding: 0,
      })}
      aria-label={t('edit_phone_aria', { name: location.name })}
    >
      <i className="fa fa-pencil" aria-hidden="true" />
      {tShared('edit')}
    </button>
  )

  const body = isEditing ? (
    <div ref={editFormRef}>
      {/* Live region announces the mode transition for screen-reader
          users — clicking Edit moves focus to the input silently
          otherwise, leaving the SR user with no signal that the surface
          changed mode (per WCAG 4.1.3 status-messages SC). */}
      <p className="sr-only" role="status">
        {t('phone_edit_announce')}
      </p>
      <div
        className={`d-flex ${css({
          gap: '16px',
          width: '100%',
          alignItems: 'stretch',
        })}`}
      >
        <input
          ref={phoneInputRef}
          type="tel"
          value={phoneDraft}
          onChange={e => {
            setPhoneDraft(e.target.value)
            if (phoneError) setPhoneError(null)
          }}
          onKeyDown={e => {
            if (e.key === 'Enter') handleSave()
          }}
          aria-label={t('phone_input_aria')}
          aria-invalid={!!phoneError}
          // Only set aria-describedby when an error exists — a dangling
          // describedby id is itself a WCAG 4.1.2 violation.
          aria-describedby={phoneError ? 'phone-error' : undefined}
          className={css({
            flex: '1 0 0',
            minWidth: 0,
            padding: '14px 16px',
            border: `1px solid ${
              phoneError ? palette.cayenne50 : palette.belizeHole
            }`,
            borderRadius: '4px',
            fontSize: '16px',
            lineHeight: '20px',
            color: palette.grey90,
            fontFamily: 'Lato, sans-serif',
            outline: 'none',
          })}
          disabled={isSaving}
        />
        <Button kind={Kind.PRIMARY} onClick={handleSave} disabled={isSaving}>
          {t('phone_save')}
        </Button>
      </div>
      {phoneError && (
        <p
          id="phone-error"
          className={`mt-xs mb-0 ${css({
            color: palette.cayenne50,
            fontSize: '14px',
            lineHeight: '20px',
          })}`}
          role="alert"
        >
          {phoneError}
        </p>
      )}
    </div>
  ) : (
    <span>
      {location.phone ? `1 ${phoneNumberFormatter(location.phone)}` : '—'}
    </span>
  )

  return { trailing, body }
}

export default usePhoneField
